import { ViewerSettings } from '../types/settings';

type LanguageCode = ViewerSettings['language'];

// Language code mapping for MyMemory API
const languageCodeMap: Record<LanguageCode, string> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  zh: 'zh-CN',
};

class TranslationService {
  private cache = new Map<string, string>();

  constructor() {
    // No API keys required - uses free MyMemory Translation API
  }

  async translateText(text: string, targetLanguage: LanguageCode, sourceLanguage: LanguageCode = 'en'): Promise<string> {
    const normalized = text?.trim();
    if (!normalized) return text;
    if (targetLanguage === sourceLanguage) return text;

    const cacheKey = `${sourceLanguage}:${targetLanguage}:${normalized}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const translated = await this.translateViaApi(normalized, sourceLanguage, targetLanguage);
    const finalText = translated || text;
    this.cache.set(cacheKey, finalText);
    return finalText;
  }

  async translateBatch(items: string[], targetLanguage: LanguageCode, sourceLanguage: LanguageCode = 'en'): Promise<string[]> {
    if (items.length === 0 || targetLanguage === sourceLanguage) return items;

    return Promise.all(items.map((item) => this.translateText(item, targetLanguage, sourceLanguage)));
  }

  private async translateViaApi(text: string, sourceLanguage: LanguageCode, targetLanguage: LanguageCode): Promise<string | null> {
    try {
      const sourceLang = languageCodeMap[sourceLanguage];
      const targetLang = languageCodeMap[targetLanguage];

      const response = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${sourceLang}|${targetLang}`
      );

      if (!response.ok) {
        throw new Error(`Translation API failed (${response.status})`);
      }

      const data = (await response.json()) as any;
      
      // MyMemory returns translations in data.responseData.translatedText
      if (data.responseData?.translatedText) {
        return data.responseData.translatedText;
      }
      
      return null;
    } catch (error) {
      console.warn('Translation request failed, using source text:', error);
      return null;
    }
  }
}

export const translationService = new TranslationService();
